/**
 * Reset a page passcode — random or custom. Shared by campaign pages,
 * advertiser pages and creator dashboards (the caller supplies the API call
 * and reports its errors). The current PIN is masked until asked for, so a
 * screen share doesn't show it; the weak-PIN check mirrors the backend.
 */

import React, { useId, useState } from 'react';
import { Button, Modal } from '../../ui';
import { fieldStyle, pinProblem } from './shared';

interface ResetPinModalProps {
  eyebrow: string;
  label: string;
  currentPin: string;
  /** Extra sentence about what the PIN opens (advertiser pages also unlock every child page). */
  scopeNote?: string;
  onClose: () => void;
  /** Runs the reset; undefined pin = let the server generate one. Errors are handled by the caller. */
  onSubmit: (pin: string | undefined) => Promise<void>;
}

const ResetPinModal: React.FC<ResetPinModalProps> = ({ eyebrow, label, currentPin, scopeNote, onClose, onSubmit }) => {
  const [mode, setMode] = useState<'random' | 'custom'>('random');
  const [customPin, setCustomPin] = useState('');
  const [resetting, setResetting] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const radioName = useId();
  const customIssue = mode === 'custom' ? pinProblem(customPin) : null;

  const run = async () => {
    if (customIssue) return;
    setResetting(true);
    try {
      await onSubmit(mode === 'custom' ? customPin : undefined);
    } finally {
      setResetting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow={eyebrow}
      title={`Reset PIN — ${label}`}
      ariaLabel={`Reset PIN for ${label}`}
      width={480}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={run} disabled={resetting || !!customIssue}>
            {resetting ? 'Resetting…' : 'Reset PIN'}
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Current PIN: <code style={{ fontWeight: 700, letterSpacing: 2, minWidth: 44 }}>{showCurrent ? currentPin : '••••'}</code>
          <Button size="sm" variant="ghost" onClick={() => setShowCurrent((v) => !v)}>
            {showCurrent ? 'Hide' : 'Show'}
          </Button>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="radio" name={radioName} checked={mode === 'random'} onChange={() => setMode('random')} />
          Generate a random new PIN
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input type="radio" name={radioName} checked={mode === 'custom'} onChange={() => setMode('custom')} />
          Custom:
          <input
            aria-label="Custom PIN"
            style={{ ...fieldStyle, width: 96, letterSpacing: 4, fontFamily: 'var(--font-mono)', opacity: mode === 'custom' ? 1 : 0.6 }}
            value={customPin}
            maxLength={4}
            inputMode="numeric"
            onFocus={() => setMode('custom')}
            onChange={(e) => {
              setMode('custom');
              setCustomPin(e.target.value.replace(/\D/g, '').slice(0, 4));
            }}
          />
        </label>
        {customIssue && customPin.length === 4 && <div style={{ color: 'var(--danger)', fontSize: 12.5 }}>{customIssue}</div>}
        <div style={{ color: 'var(--fg-3)', fontSize: 12.5 }}>
          The client’s saved passcode stops working on every device — they’ll be prompted for the new one on their next
          visit. This also clears any lockout.{scopeNote ? ` ${scopeNote}` : ''}
        </div>
      </div>
    </Modal>
  );
};

export default ResetPinModal;
